import { type Nullable } from '@Types';
import { type SaveProjectProgressArgs, type SavedProjectProgress } from './ProjectFieldsModal.types';

// The snake_case payload. It matches the strong params that
// Api::ProjectProgressController#create_or_update permits.
type ProjectProgressRecord = {
    id?: Nullable<string>;
    user_id: string;
    requirement_identifier: string;
    learner_project_id: string;
    program_type?: Nullable<string>;
    score: Nullable<number>;
    feedback: Nullable<string>;
    status: Nullable<string>;
    id_verified: Nullable<boolean>;
    waived: boolean;
    resubmission_required: boolean;
    allow_resubmission: boolean;
    resubmission_due_date: Nullable<number>;
    project_submission_similarity_and_originality_status: Nullable<string>;
    project_submission_needs_admin_review: boolean;
    project_submission_private_notes: Nullable<string>;
    project_submission_selected_project_variant: Nullable<string>;
    project_submission_presentation_transcript: Nullable<string>;
    // The provisional grade lives on the project submission, not the progress record.
    // See https://github.com/quanticedu/back_royal/pull/17209.
    project_submission_provisional_score: Nullable<number>;
    project_submission_provisional_feedback: Nullable<string>;
};

export function buildProjectProgressRecord({
    id,
    userId,
    requirementIdentifier,
    learnerProjectId,
    programType,
    fields,
}: SaveProjectProgressArgs): ProjectProgressRecord {
    return {
        ...(id ? { id } : {}),
        user_id: userId,
        requirement_identifier: requirementIdentifier,
        learner_project_id: learnerProjectId,
        // Always sent, even when null, as the original did. The server logs project:graded with it.
        program_type: programType ?? null,
        score: fields.score,
        feedback: fields.feedback,
        status: fields.status,
        id_verified: fields.idVerified,
        waived: fields.waived,
        resubmission_required: fields.resubmissionRequired,
        allow_resubmission: fields.allowResubmission,
        resubmission_due_date: fields.resubmissionDueDate,
        project_submission_similarity_and_originality_status: fields.projectSubmissionSimilarityAndOriginalityStatus,
        project_submission_needs_admin_review: fields.projectSubmissionNeedsAdminReview,
        project_submission_private_notes: fields.projectSubmissionPrivateNotes,
        project_submission_selected_project_variant: fields.projectSubmissionSelectedProjectVariant,
        project_submission_presentation_transcript: fields.projectSubmissionPresentationTranscript,
        project_submission_provisional_score: fields.provisionalScore,
        project_submission_provisional_feedback: fields.provisionalFeedback,
    };
}

// POSTs the grading record to the project_progress endpoint, as
// GradingPortal/SAndOReview/useBatchUpdate.ts does. It returns the updated record the server
// rendered. On failure it throws an Error with a message for the user.
export async function saveProjectProgress(args: SaveProjectProgressArgs): Promise<SavedProjectProgress> {
    const record = buildProjectProgressRecord(args);

    let response: Response;
    try {
        response = await fetch('/api/project_progress.json', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ record }),
        });
    } catch {
        throw new Error('Network error: Please check your connection and try again.');
    }

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to save project progress');
    }

    const data = await response.json();
    const saved = data?.contents?.project_progress?.[0];
    if (!saved) {
        throw new Error('Failed to save project progress');
    }
    return saved as SavedProjectProgress;
}
